import { Glue42Core } from "@glue42/core";
import callbackRegistry, { CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Glue42 } from "../../glue";
import { isBoolean, isNullOrWhiteSpace, isUndefinedOrNull } from "../activity/helpers/util";
import { CompositionChangedData } from "./environment/gd/types";
import groupFactory from "./group";
import { default as windowStore } from "./store";
import {
    Environment,
    GroupsInternal,
    GroupsWrapper,
    GroupWrapper,
    WindowWrapper
} from "./types";
import executor from "./environment/gd/executor";

export default (environment: Environment, logger: Glue42Core.Logger.API): GroupsWrapper => {
    const _registry: CallbackRegistry = callbackRegistry();
    const _groups: { [key: string]: GroupWrapper } = {};
    let heardForWindowsCounter = -1;

    // Getting already existing windows and create Group API for each window
    const windows = windowStore.list;
    Object.keys(windows).forEach((k) => {
        const win = windows[k];
        const groupId = win.API.groupId;
        const winId = win.API.id;
        // hidden windows doesn't have groupId
        if (!isNullOrWhiteSpace(groupId)) {
            addWindow(groupId, winId);
        }
    });

    windowStore.onRemoved((w) => {
        const group = findGroupWrapperByWindow(w.API);
        removeWindow(group, w);
    });

    environment.onCompositionChanged((windowInfo: CompositionChangedData) => {
        handleCompositionChanged(windowInfo);
    });

    environment.onGroupHeaderVisibilityChanged((windowInfo) => {
        const windowId = windowInfo.windowId;
        const group: Glue42.Windows.Group = findGroupByWindow(windowId);
        if (typeof group !== "undefined") {
            const groupEvents: GroupWrapper = _groups[group.id];

            if (heardForWindowsCounter === -1) {
                heardForWindowsCounter = group.windows.length;
            }

            heardForWindowsCounter--;
            if (heardForWindowsCounter === 0) {
                heardForWindowsCounter = -1;
                groupEvents.groupInternal.handleGroupHeaderVisibilityChanged(windowInfo);
            }
        }
    });

    environment.onGroupVisibilityChanged((data) => {
        const groupEvents: GroupWrapper = _groups[data.groupId];
        if (groupEvents) {
            groupEvents.groupInternal.handleGroupVisibilityChanged(data.visible);
        }
    });

    environment.onGroupStateChanged((data) => {
        const groupWrapper = _groups[data.groupId];
        if (data.state === "hibernated") {
            if (groupWrapper && groupWrapper.groupAPI) {
                groupWrapper.groupInternal.handleGroupHibernateChanged(true);
            }
            _registry.execute("group-hibernated", data.groupId);
        } else if (data.state === "resumed") {
            if (groupWrapper && groupWrapper.groupAPI) {
                groupWrapper.groupInternal.handleGroupHibernateChanged(false);
            }
            _registry.execute("group-resumed", groupWrapper.groupAPI);
        }
    });

    // Methods

    function my(): Glue42.Windows.Group {
        return findGroupByWindow(environment.my());
    }

    function list(success?: (groups: Glue42.Windows.Group[]) => void): Glue42.Windows.Group[] {
        const result: Glue42.Windows.Group[] = Object.keys(_groups).map((groupId) => {
            if (_groups[groupId]) {
                return _groups[groupId].groupAPI;
            }

        });

        if (typeof success === "function") {
            success(result);
        }
        return result;
    }

    function findGroupByWindow(winId: string | Glue42.Windows.GDWindow, success?: (group: Glue42.Windows.Group) => void, error?: (error: string) => void): Glue42.Windows.Group {
        let windowId: string;
        if (typeof winId === "string") {
            windowId = winId;
        } else if (!isUndefinedOrNull(winId)) {
            windowId = winId.id;
        }
        const result = Object.values(_groups).find((groupObj) => {
            const group: Glue42.Windows.Group = groupObj.groupAPI;
            const wins = group.windows.filter((w) => w.id === windowId);
            return wins.length;
        });
        if (result) {
            if (typeof success === "function") {
                success(result.groupAPI);
            }
            return result.groupAPI;
        } else {
            if (typeof error === "function") {
                error(`Cannot find the group of the window.`);
            }
        }
    }

    function waitForGroup(groupId: string): Promise<Glue42.Windows.Group> {
        if (!groupId) {
            return Promise.reject(`groupId must be defined`);
        }
        return new Promise((res, rej) => {
            const groupWrapper = _groups[groupId];
            if (groupWrapper) {
                res(groupWrapper.groupAPI);
            } else {
                const un = onGroupAdded((group) => {
                    if (group.id === groupId) {
                        un();
                        res(group);
                    }
                });
            }
        });
    }

    async function resume(groupId: string, activate?: boolean): Promise<void> {
        if (!isUndefinedOrNull(activate) && !isBoolean(activate)) {
            throw new Error("Activate flag must be a boolean!");
        }
        activate = !isUndefinedOrNull(activate) ? activate : true;
        await executor.executeGroup("resumeGroup", {
            groupId,
            options: { activate }
        });
    }

    async function hibernate(groupId: string): Promise<string> {
        await executor.executeGroup("hibernateGroup", {
            groupId
        });
        return groupId;
    }

    // Events
    function onGroupAdded(callback: (group: Glue42.Windows.Group) => void): UnsubscribeFunction {
        return _registry.add("group-added", callback);
    }

    function onGroupRemoved(callback: (group: Glue42.Windows.Group) => void): UnsubscribeFunction {
        return _registry.add("group-removed", callback);
    }

    function onWindowMoved(callback?: (windowId: string, oldTabGroupId: string, newGroupId: string) => void): UnsubscribeFunction {
        return _registry.add("window-moved", callback);
    }

    function onHibernated(callback?: (groupId: string) => void): () => void {
        if (!callback) {
            throw new Error("Callback argument is required");
        }
        if (callback && typeof callback !== "function") {
            throw new Error("Callback argument must be a function")
        }
        return _registry.add("group-hibernated", callback);
    }

    function onResumed(callback?: (group: Glue42.Windows.Group) => void): () => void {
        if (!callback) {
            throw new Error("Callback argument is required");
        }
        if (callback && typeof callback !== "function") {
            throw new Error("Callback argument must be a function")
        }
        return _registry.add("group-resumed", callback);
    }

    // Private
    function createOrGet(groupId: string): GroupWrapper {
        if (!_groups.hasOwnProperty(groupId)) {

            const createdGroupWrapper: GroupWrapper = groupFactory(groupId, environment.executor);
            _groups[groupId] = createdGroupWrapper;

            const group = createdGroupWrapper.groupAPI;
            _registry.execute("group-added", group);

            return createdGroupWrapper;
        } else {
            return _groups[groupId];
        }
    }

    function deleteIfEmpty(groupWrapper: GroupWrapper): void {
        const group = groupWrapper.groupAPI;
        if (group.windows.length === 0) {
            delete _groups[group.id];
            executor.clearCallbacks(group.id);
            _registry.execute("group-removed", group);
        }
    }

    // hidden window doesn't have a groupId
    function addWindow(groupId: string, winId: string) {
        const group = createOrGet(groupId);
        group.groupInternal.addWindow(winId);
        return group;
    }

    function removeWindow(group: GroupWrapper, win: WindowWrapper) {
        if (!group) {
            return;
        }
        group.groupInternal.removeWindow(win);
        deleteIfEmpty(group);
    }

    function handleCompositionChanged(state: CompositionChangedData): void {
        const groupId = state.groupId;
        const windowId = state.windowId;
        const win = windowStore.get(windowId);
        if (!win) {
            return;
        }
        const currentGroup = findGroupWrapperByWindow(win.API);
        // if the window's visibility is changed to hidden, remove from the group
        // or the window is closed.
        if (isUndefinedOrNull(groupId)) {
            removeWindow(currentGroup, win);
            return;
        }
        // if the window's visibility is changed to visible, addOrCreate the group
        if (isUndefinedOrNull(currentGroup) && !isUndefinedOrNull(groupId)) {
            addWindow(groupId, win.API.id);
            return;
        }

        if (currentGroup.groupAPI.id !== groupId) {
            moveWindow(win, currentGroup.groupAPI.id, groupId);
        }
    }

    function moveWindow(win: WindowWrapper, from: string, to: string) {
        const winId = win.API.id;
        const fromGroup = _groups[from];
        removeWindow(fromGroup, win);
        const toGroup = addWindow(to, winId);
        win.Events.handleGroupChanged(toGroup.groupAPI, fromGroup.groupAPI);
        _registry.execute("window-moved", winId, from, to);
    }

    function findGroupWrapperByWindow(winId: string | Glue42.Windows.GDWindow): GroupWrapper {
        let windowId: string;
        if (typeof winId === "string") {
            windowId = winId;
        } else if (!isUndefinedOrNull(winId)) {
            windowId = winId.id;
        }
        return Object.values(_groups).find((groupObj) => {
            const groupInternal = groupObj.groupInternal;
            const wins = groupInternal.windows.filter((id) => id === windowId);
            return wins.length;
        });
    }

    // #region "API"

    const groups = {
        get my() {
            return my();
        },
        list,
        findGroupByWindow,
        waitForGroup,
        onGroupAdded,
        onGroupRemoved,
        hibernate,
        resume,
        onHibernated,
        onResumed
    };

    // #endregion "API

    const events: GroupsInternal = { onWindowMoved };

    return {
        groupsAPI: groups,
        groupsEvents: events,
    };
};
